import React from 'react';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { mustRefreshAuthenticationSelector } from '@redux/authentication';
import { bookingSelector } from '@redux/booking/selectors';
import { colorsSelector } from '@redux/colorPalette/selectors';
import { bamIdSelector } from '@redux/room/selectors';
import { shouldSkipStaffPasswordSelector } from '@redux/session/selectors';
import { Auth } from 'aws-amplify';
import analytics from 'lib/analytics';
import socket from 'lib/socket/socket';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useBoolean, useLongPress } from 'react-use';

import { LogoButton, SessionEndLogoButtonContainer } from 'components/b2b/Logo/Logo.style';
import { PATHS } from 'constants/pathsB2b';
import { useClearSession } from 'services/hooks/useClearSession';
import { useTvToIpadMultiScreenEventsListener } from 'services/hooks/useMultiScreenEventListener';
import { updateBookingStatus } from 'services/networking/booking';
import { BookingStatus } from 'shared-types/booking';
import { PlayerEvent } from 'shared-types/player-event';

import {
  Close,
  MessageBlock,
  PageContainer,
  TslLogoComponent,
  PrimaryMessage,
  SecondaryMessage,
  ActionWrapper,
} from './SessionEnd.style';

type SessionEndProps = {
  onLogoLongPress: () => void;
};

const SessionEnd: React.FunctionComponent<SessionEndProps> = ({ onLogoLongPress }) => {
  const navigate = useNavigate();
  const mustRefreshAuthentication = useSelector(mustRefreshAuthenticationSelector);
  const shouldSkipStaffPassword = useSelector(shouldSkipStaffPasswordSelector);
  const bamRoomId = useSelector(bamIdSelector);
  const currentBooking = useSelector(bookingSelector);
  const colors = useSelector(colorsSelector);
  const clearSession = useClearSession();
  const [on, setOn] = useBoolean(false);

  const [finishedSession, setFinishedSession] = useBoolean(false);

  const longPressProps = useLongPress(onLogoLongPress);

  useTvToIpadMultiScreenEventsListener({
    [PlayerEvent.SET_SESSION_END_OVERLAY]: () => {
      setOn(true);
    },
    [PlayerEvent.END_SESSION]: () => {
      setFinishedSession(true);
    },
  });

  return (
    <ActionWrapper
      $on={on}
      {...{ colors }}
      onClick={() => {
        if (!finishedSession) {
          return;
        }

        if (bamRoomId) {
          socket.emit('tslSessionEnd', { roomId: bamRoomId });
        }
        if (currentBooking) {
          updateBookingStatus(currentBooking.bookingId, BookingStatus.COMPLETED);
        }

        analytics.track('session_end', {
          finishedSession,
          bamRoomId,
          currentBookingId: currentBooking?.bookingId,
          bookingStartHour: currentBooking?.startHour,
          bookingEndHour: currentBooking?.endHour,
        });

        clearSession();

        if (mustRefreshAuthentication) {
          Auth.signOut();
        } else {
          shouldSkipStaffPassword ? navigate(PATHS.SESSION) : navigate(PATHS.STAFF);
        }
      }}
    >
      <PageContainer>
        <Close $on={finishedSession} aria-label='close'>
          <CloseRoundedIcon fontSize='inherit' />
        </Close>
        <MessageBlock {...{ colors }}>
          <PrimaryMessage>
            <FormattedMessage id='session-end.primary-message' />
          </PrimaryMessage>
          <SecondaryMessage>
            <FormattedMessage id='session-end.secondary-message' />
          </SecondaryMessage>
        </MessageBlock>
        <SessionEndLogoButtonContainer {...longPressProps}>
          <LogoButton />
          <TslLogoComponent />
        </SessionEndLogoButtonContainer>
      </PageContainer>
    </ActionWrapper>
  );
};

export default SessionEnd;
